// Merchant Sales Report (MM-3037): tree table following Figma "Zood Pay AMS - Merchant Sales Report"
// (Zoodpay_Web-Recharge-Clients, frames 3071:18004 and 3102:12759). Rows are a fixed 48px, so only the rows in
// view are mounted (windowing) however many merchants have been expanded.
import { memo, ReactNode, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { getFirefoxCssFix } from "../../../components/commonfunctions/getFirefoxCssFix";
import ToolTip from "../../../components/ToolTip/ToolTip";
import { formatPuls } from "./format";
import { TreeStore, VisibleRow } from "./treeStore";
import { Figures, MERCHANT_TYPE_LABELS, MerchantRow, Mno } from "./types";

export const ROW_HEIGHT = 48;
const OVERSCAN = 12;
const FALLBACK_VIEWPORT = 600;
// Figma column widths.
const MERCHANT_W = 380;
const CODE_W = 140;
const PARENT_W = 140;
const MNO_W = 105;
const RECHARGE_W = 150;
const STOCK_W = 126;
const INDENT_W = 24;
// The two totals columns stay pinned to the right edge while the rest of the table scrolls sideways.
const RECHARGE_RIGHT = STOCK_W;
const STOCK_RIGHT = 0;

const COLORS = {
  header: "#fff9e5",
  totalCell: "#fff2c4",
  expandedRow: "#fcfaf2",
  subtotalRow: "#fff9e5",
  grandTotal: "#f1c92d",
  onPrimary: "#332b00",
  connector: "#aab4c5",
  chevronBorder: "#e5e9eb",
  chevronBorderOpen: "#ffe68a",
  text: "#0b1739",
};

const DASH = "—";
// Shown as the parent of top-level merchants (parent_id is null).
const ZOODPAY = "ZoodPay";

interface MerchantSalesTableProps {
  rows: VisibleRow[];
  store: TreeStore;
  mnos: Mno[];
  grandTotal: Figures | null;
  /** Scroll back to the top whenever this changes (new merchant or date range). */
  resetKey: string;
  onToggle: (id: string) => void;
  onLoadMore: (parentId: string) => void;
}

const MerchantSalesTable = ({ rows, store, mnos, grandTotal, resetKey, onToggle, onLoadMore }: MerchantSalesTableProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(FALLBACK_VIEWPORT);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [resetKey]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setViewportHeight(el.clientHeight || FALLBACK_VIEWPORT));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const width = MERCHANT_W + CODE_W + 2 * PARENT_W + mnos.length * MNO_W + RECHARGE_W + STOCK_W;
  const start = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const end = Math.min(rows.length, Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN);

  return (
    <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-outline bg-white" data-testid="merchant-sales-table">
      <div
        ref={scrollRef}
        className={`min-h-0 flex-1 overflow-auto ${getFirefoxCssFix("scroll")}`}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      >
        <div style={{ width, minWidth: "100%" }}>
          <div className="sticky top-0 z-10 flex border-b border-outline" style={{ background: COLORS.header, height: ROW_HEIGHT }}>
            <HeaderCell width={MERCHANT_W}>MERCHANT</HeaderCell>
            <HeaderCell width={CODE_W}>USER CODE</HeaderCell>
            <HeaderCell width={PARENT_W}>PARENT NAME</HeaderCell>
            <HeaderCell width={PARENT_W}>PARENT CODE</HeaderCell>
            {mnos.map((mno) => (
              <HeaderCell key={mno.id} width={MNO_W} align="right">{mno.name.toUpperCase()}</HeaderCell>
            ))}
            <HeaderCell width={RECHARGE_W} align="right" background={COLORS.totalCell} stickyRight={RECHARGE_RIGHT}>TOTAL RECHARGE SALES</HeaderCell>
            <HeaderCell width={STOCK_W} align="right" background={COLORS.totalCell} stickyRight={STOCK_RIGHT}>TOTAL STOCK SALES</HeaderCell>
          </div>

          <div className="relative" style={{ height: rows.length * ROW_HEIGHT }}>
            {rows.slice(start, end).map((visibleRow, k) => {
              const top = (start + k) * ROW_HEIGHT;
              if (visibleRow.kind === "node" || visibleRow.kind === "subtotal") {
                const row = store.byId.get(visibleRow.id);
                if (!row) return null;
                return (
                  <TreeRow
                    key={`${visibleRow.kind}:${visibleRow.id}`}
                    kind={visibleRow.kind}
                    row={row}
                    depth={visibleRow.depth}
                    top={top}
                    expanded={store.expanded.has(row.id)}
                    loading={store.loading.has(row.id) && !store.hasChildrenLoaded(row.id)}
                    onToggle={onToggle}
                  />
                );
              }
              return (
                <div
                  key={`${visibleRow.kind}:${visibleRow.parentId}`}
                  className="absolute left-0 right-0 flex items-center border-b border-outline bg-white text-sm"
                  style={{ top, height: ROW_HEIGHT }}
                >
                  <div className="flex h-full items-center gap-2 pl-3 pr-2" style={{ width: MERCHANT_W }}>
                    <Connectors depth={visibleRow.depth} />
                    {visibleRow.kind === "loading" ? (
                      <span className="flex items-center gap-2 text-secondary_text">
                        <Loader2 size={14} className="animate-spin" /> Loading…
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="font-medium text-p_primary hover:underline"
                        onClick={() => onLoadMore(visibleRow.parentId)}
                      >
                        Load more ({visibleRow.loaded.toLocaleString()} of {visibleRow.total.toLocaleString()})
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pinned so it stays visible while scrolling (ticket). Served by the API, never summed here. */}
          <div
            className="sticky bottom-0 z-10 flex border-t border-outline font-bold"
            style={{ background: COLORS.grandTotal, color: COLORS.onPrimary, height: ROW_HEIGHT }}
            data-testid="merchant-sales-grand-total"
          >
            <Cell width={MERCHANT_W} className="pl-3 text-sm">Σ GRAND TOTAL</Cell>
            <Cell width={CODE_W}>Across network</Cell>
            <Cell width={PARENT_W}>{DASH}</Cell>
            <Cell width={PARENT_W}>{DASH}</Cell>
            {mnos.map((mno, m) => (
              <Cell key={mno.id} width={MNO_W} align="right">{grandTotal ? formatPuls(grandTotal.amounts[m] ?? 0) : DASH}</Cell>
            ))}
            <Cell width={RECHARGE_W} align="right" background={COLORS.grandTotal} stickyRight={RECHARGE_RIGHT}>
              {grandTotal ? formatPuls(grandTotal.total) : DASH}
            </Cell>
            <Cell width={STOCK_W} align="right" background={COLORS.grandTotal} stickyRight={STOCK_RIGHT}>
              {grandTotal ? formatPuls(grandTotal.stock_sales) : DASH}
            </Cell>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CellProps {
  width: number;
  /** Text columns are left-aligned and number columns right-aligned, as in the other tables. */
  align?: "left" | "right";
  background?: string;
  color?: string;
  className?: string;
  /** Pins the cell this many px from the right edge of the scroll area (needs an opaque background). */
  stickyRight?: number;
  children?: ReactNode;
}

const stickyStyle = (right?: number) => (right === undefined ? undefined : { position: "sticky" as const, right, zIndex: 1 });

// ToolTip renders its text as HTML, so merchant names from the API are escaped first.
const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

/** One line, cut with an ellipsis, with the shared table hover tooltip (as in TableBody). The parent must be `group relative`. */
const TruncatedText = ({ text, align = "left", className = "" }: { text: string; align?: "left" | "right"; className?: string }) => (
  <>
    <span className={`block min-w-0 max-w-full truncate ${className}`}>{text}</span>
    <ToolTip
      ToolTipItem={escapeHtml(text)}
      top="-top-6"
      left={align === "right" ? "right-0" : "left-0"}
      width="w-max"
      bgColor="bg-gray-800"
      textColor="text-white"
    />
  </>
);

const HeaderCell = ({ width, align = "left", background, stickyRight, children }: CellProps) => (
  <div
    className={`flex h-full shrink-0 items-center px-2 text-[10px] font-extrabold ${align === "right" ? "justify-end text-right" : "justify-start text-left"}`}
    style={{ width, background, color: COLORS.onPrimary, ...stickyStyle(stickyRight) }}
  >
    {children}
  </div>
);

// No overflow-hidden here: the hover tooltip sits above the cell; the text itself is truncated.
const Cell = ({ width, align = "left", background, color, className = "", stickyRight, children }: CellProps) => (
  <div
    className={`group relative flex h-full shrink-0 items-center whitespace-nowrap px-2 text-[13px] tabular-nums ${align === "right" ? "justify-end text-right" : "justify-start text-left"} ${className}`}
    style={{ width, background, color, ...stickyStyle(stickyRight) }}
  >
    {typeof children === "string" ? <TruncatedText text={children} align={align} /> : children}
  </div>
);

/** One 24px guide column per ancestor level (Figma "Connector n"). */
const Connectors = ({ depth }: { depth: number }) => (
  <>
    {Array.from({ length: depth }, (_, i) => (
      <div key={i} className="relative h-full shrink-0" style={{ width: INDENT_W }}>
        <div className="absolute left-3 top-0 h-full w-px" style={{ background: COLORS.connector }} />
      </div>
    ))}
  </>
);

interface TreeRowProps {
  kind: "node" | "subtotal";
  row: MerchantRow;
  depth: number;
  top: number;
  expanded: boolean;
  loading: boolean;
  onToggle: (id: string) => void;
}

// Memoized on primitive props so expanding one merchant doesn't re-render every mounted row.
const TreeRow = memo(({ kind, row, depth, top, expanded, loading, onToggle }: TreeRowProps) => {
  const isSubtotal = kind === "subtotal";
  const hasChildren = row.child_count > 0;
  // A merchant's row is always its own sales only (collapsed or expanded), never its direct or indirect
  // children's. Group totals appear only on the Σ Subtotal row (or the Grand Total for the selected merchant).
  const figures = isSubtotal ? row.subtree : row.own;
  const background = isSubtotal ? COLORS.subtotalRow : expanded ? COLORS.expandedRow : "#ffffff";

  return (
    <div
      className={`absolute left-0 right-0 flex border-b border-outline ${isSubtotal ? "font-bold" : ""}`}
      style={{ top, height: ROW_HEIGHT, background, color: COLORS.text }}
      data-testid={isSubtotal ? `subtotal-${row.id}` : `row-${row.id}`}
    >
      <div className="flex h-full shrink-0 items-center gap-2 pl-3 pr-2" style={{ width: MERCHANT_W }}>
        <Connectors depth={depth} />
        {isSubtotal ? null : hasChildren ? (
          <button
            type="button"
            aria-label={expanded ? `Collapse ${row.name}` : `Expand ${row.name}`}
            aria-expanded={expanded}
            className="flex size-6 shrink-0 items-center justify-center rounded-md border"
            style={{
              borderColor: expanded ? COLORS.chevronBorderOpen : COLORS.chevronBorder,
              background: expanded ? COLORS.expandedRow : "#ffffff",
            }}
            onClick={() => onToggle(row.id)}
            disabled={loading}
          >
            {loading ? (
              <Loader2 size={14} className="animate-spin" aria-label="Loading" />
            ) : expanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>
        ) : (
          // Same 24px slot as the expand button, so names line up whether or not a merchant has children.
          <span className="flex size-6 shrink-0 items-center justify-center" aria-hidden="true">
            <span className="size-[7px] rounded-full" style={{ background: COLORS.connector }} />
          </span>
        )}
        <div className="group relative min-w-0 flex-1">
          <TruncatedText
            className={`text-sm ${isSubtotal || expanded || depth === 0 ? "font-semibold" : "font-normal"}`}
            text={isSubtotal ? `Σ Subtotal of ${row.name}` : row.name}
          />
        </div>
      </div>
      <Cell width={CODE_W}>{isSubtotal ? MERCHANT_TYPE_LABELS[row.user_type] ?? row.user_type : row.user_code}</Cell>
      <Cell width={PARENT_W}>{isSubtotal ? DASH : row.parent_name ?? (row.parent_id ? DASH : ZOODPAY)}</Cell>
      <Cell width={PARENT_W}>{isSubtotal ? DASH : row.parent_code ?? DASH}</Cell>
      {figures.amounts.map((amount, m) => (
        <Cell key={m} width={MNO_W} align="right" className={isSubtotal ? "font-bold" : ""}>{formatPuls(amount)}</Cell>
      ))}
      <Cell width={RECHARGE_W} align="right" background={COLORS.totalCell} color={COLORS.onPrimary} className="font-bold" stickyRight={RECHARGE_RIGHT}>
        {formatPuls(figures.total)}
      </Cell>
      <Cell width={STOCK_W} align="right" background={COLORS.totalCell} color={COLORS.onPrimary} className="font-bold" stickyRight={STOCK_RIGHT}>
        {formatPuls(figures.stock_sales)}
      </Cell>
    </div>
  );
});

export default MerchantSalesTable;

// Loading placeholder shaped like the table (full width, header band, shimmer rows), like GridTable's skeleton.
export const MerchantSalesTableSkeleton = ({ rows = 10, columns = 8 }: { rows?: number; columns?: number }) => (
  <div className="overflow-hidden rounded-lg border border-outline bg-white" data-testid="merchant-sales-skeleton">
    {Array.from({ length: rows + 1 }).map((_, r) => (
      <div
        key={r}
        className="flex border-b border-outline last:border-b-0"
        style={{ height: ROW_HEIGHT, background: r === 0 ? COLORS.header : undefined }}
      >
        {Array.from({ length: columns }).map((_, c) => (
          <div key={c} className={`flex items-center px-3 ${c === 0 ? "flex-[2]" : "flex-1"}`}>
            <div className={`w-full animate-pulse rounded bg-gray-200 ${r === 0 ? "h-3" : "h-4"}`} />
          </div>
        ))}
      </div>
    ))}
  </div>
);
